import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { faBarsFilter, faSearch } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Icon, InputText, Spinner, Text, toast } from "@performyard/py-components";

import AccordionSectionHeader from "./AccordionSectionHeader";
import MeetingGoalListItem from "./MeetingGoalListItem";

import type { MeetingGoalAction } from "../types";

import {
	useAddMeetingAgendaItem,
	useMeetingAgendaItems,
	useRbacPrimaryRolePermissions,
	useSearchService,
} from "../../../../api/hooks";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { useGoalActionModals, useMeetingGoalsVisibility } from "../hooks";

import { SERVICE_GOAL } from "../../../../api/services/defs/search-service";
import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { getMeetingGoalsSearchFilters } from "../utils";

import type { EmployeeGoalsFilter } from "../../../Goals/Employee/Filters";
import EmployeeGoalsPanel from "../../../Goals/Employee/Filters";
import { config } from "../config";
import { useMeetingContext } from "../context";
import { meetingDetailsQueryKeys } from "../query-keys";

const MeetingGoalsAccordion = (): React.ReactElement | null => {
	const navigate = useNavigate();
	const {
		isInteractionDisabled,
		meeting,
		userId,
		isAdmin = false,
		isManagerDirectReportMeeting,
	} = useMeetingContext();
	const queryClient = useQueryClient();
	const [isExpanded, setIsExpanded] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [filters, setFilters] = useState<Partial<EmployeeGoalsFilter>>({ status: ["active"] });
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const { data: goalPermissions } = useRbacPrimaryRolePermissions("goal");
	const canUserCreateGoals = goalPermissions?.includes("goals:createGoal");

	const debouncedSearchValue = useDebouncedValue(searchValue, 300);
	const meetingParticipantIds = useMemo(() => {
		return meeting.participants?.map((participant) => participant.userId).filter(Boolean) || [];
	}, [meeting.participants]);

	const { visibleAssigneeIds, isLoading: isVisibilityLoading } = useMeetingGoalsVisibility({
		currentUserId: userId,
		meetingParticipantIds,
		isAdmin,
	});
	const hasVisibleAssignees = visibleAssigneeIds.length > 0;

	const searchFilters = useMemo(
		() => getMeetingGoalsSearchFilters(filters, debouncedSearchValue, visibleAssigneeIds, userId),
		[filters, debouncedSearchValue, visibleAssigneeIds, userId]
	);

	const goalsQuery = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: searchFilters,
		sort: { name: "end_date", direction: "desc" },
		limit: config.goals.listLimit,
		defaultErrorMessage: "There was an error getting goals",
		disabled: !isExpanded || isVisibilityLoading || !hasVisibleAssignees,
	});

	const goals = hasVisibleAssignees ? goalsQuery.data?.results || [] : [];
	const isLoadingGoals = goalsQuery.isLoading || isVisibilityLoading;
	const { data: agendaItems } = useMeetingAgendaItems(meeting._id);
	const agendaGoalIds = useMemo(() => {
		return (agendaItems || []).map((item) => item.goalId).filter(Boolean) as string[];
	}, [agendaItems]);
	const agendaGoalIdSet = useMemo(() => new Set(agendaGoalIds), [agendaGoalIds]);

	const { mutate: addAgendaItem } = useAddMeetingAgendaItem();
	const { handleActionSelect: handleGoalActionSelect, modals } = useGoalActionModals({
		onGoalUpdated: () => goalsQuery.refetch(),
	});

	const handleToggle = useCallback((): void => {
		setIsExpanded((prev) => !prev);
	}, []);

	const handleCloseFilters = useCallback((): void => {
		setIsFiltersOpen(false);
	}, []);

	const handleApplyFilters = useCallback((newFilters: Partial<EmployeeGoalsFilter>): void => {
		setFilters(newFilters);
	}, []);

	const handleActionSelect = useCallback(
		(action: MeetingGoalAction, goal: Goal): void => {
			if (action === "addToAgenda") {
				addAgendaItem(
					{
						data: {
							name: goal.name,
							goalId: goal._id,
							meetings: [{ meetingId: meeting._id, status: "incomplete" }],
						},
					},
					{
						onSuccess: async () => {
							await queryClient.invalidateQueries({
								queryKey: meetingDetailsQueryKeys.meetingAgendaItems(meeting._id),
							});
						},
						onError: () => {
							toast.error("Failed to add goal to agenda");
						},
					}
				);
				return;
			}
			handleGoalActionSelect(action, goal);
		},
		[addAgendaItem, handleGoalActionSelect, meeting._id, queryClient]
	);

	const hasGoals = goals.length > 0;
	const shouldAllowAddToAgenda = Boolean(isManagerDirectReportMeeting) && !isInteractionDisabled;

	return (
		<div className="flex flex-col border-b border-solid border-neutral-2 bg-white">
			<AccordionSectionHeader title="Goals" isExpanded={isExpanded} onToggle={handleToggle} />

			{isExpanded && (
				<>
					<div className="flex items-center gap-2 px-4 pb-2 pt-4">
						<div className="flex-1">
							<InputText
								leftIcon={faSearch}
								value={searchValue}
								onChange={setSearchValue}
								placeholder="Search goals..."
								clearable
								data-testid="meeting-goals-search"
								aria-label="Search goals"
							/>
						</div>
						<ButtonIcon
							variant="outline"
							size={32}
							icon={faBarsFilter}
							onClick={() => setIsFiltersOpen(true)}
							disabled={isInteractionDisabled}
							aria-label="Open goal filters"
						/>
						{canUserCreateGoals && (
							<ButtonIcon
								variant="outline"
								size={32}
								icon={faPlus}
								onClick={() => navigate("/goal/wizard/")}
								disabled={isInteractionDisabled}
								aria-label="Create new goal"
							/>
						)}
					</div>

					<div
						className="flex flex-col overflow-y-auto bg-white"
						style={{ maxHeight: config.goals.maxHeight }}
					>
						{isLoadingGoals ? (
							<div className="flex items-center justify-center py-4">
								<Spinner size={config.spinnerSize.small} />
							</div>
						) : hasGoals ? (
							goals.map((goal) => (
								<MeetingGoalListItem
									key={goal._id}
									goal={goal}
									onActionSelect={handleActionSelect}
									isInteractionDisabled={isInteractionDisabled}
									includeAddToAgenda={shouldAllowAddToAgenda && !agendaGoalIdSet.has(goal._id)}
									meetingName={meeting.name}
								/>
							))
						) : (
							<div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-1">
									<Icon icon={faSearch} size={config.iconSize.large} color="neutral-7" />
								</div>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									No Goals
								</Text>
							</div>
						)}
					</div>

					<EmployeeGoalsPanel
						isOpen={isFiltersOpen}
						onClose={handleCloseFilters}
						filters={filters}
						onApplyFilters={handleApplyFilters}
					/>
				</>
			)}

			{modals}
		</div>
	);
};

export default MeetingGoalsAccordion;
